/**
 * The Mountaya API client: the Map Studio API, the Tile API and the GraphQL Data API, behind one
 * credential.
 *
 * ```ts
 * import { Mountaya } from "@mountaya/client";
 *
 * const mountaya = new Mountaya({ credential: { publishableKey: "pk_…" } });
 *
 * // A Map Studio or Tile API method resolves to the response envelope: the payload is at `data`.
 * const { data: groups } = await mountaya.studio.itineraries.getItineraries();
 * const { data: keys } = await mountaya.admin.keys.getOrganizationPublishableKeys({
 *   organization_id,
 * });
 * ```
 *
 * The Map Studio API arrives under two namespaces rather than one: `studio` is the map
 * (itineraries, zones, collections, collaboration, preferences) and `admin` is the organization
 * behind it (keys, settings, members, usage). `agent` is a third, outside both: a run outlives
 * the surface that started it and is addressed by a run id no map holds. Which one an operation
 * belongs to is declared in the contract, not decided here, so every language's client groups it
 * the same way.
 *
 * This entry point exposes the secret-key credential, which mints session tokens and must only
 * ever run on a server. Browser code imports `@mountaya/client/browser`, which does not export it
 * at all.
 */

import type { Geometry } from "geojson";

import type { ClientOptions } from "./core/rules/transport.js";
import { MountayaTransport } from "./core/rules/transport.js";
import { bind as bindData } from "./data/operations.gen.js";
import { bind as bindRest } from "./rest/operations.gen.js";
import { bind as bindTiles } from "./tiles/operations.gen.js";

export {
  SessionTokenEngine,
  type SessionTokenEngineOptions,
  type SessionTokenSnapshot,
} from "./core/engines/sessionToken.js";
export type {
  CookieCredential,
  Credential,
  PublishableCredential,
  SecretCredential,
  SessionTokenSource,
  ShareCredential,
} from "./core/rules/auth.js";
// The instants a request names in `RequestArgs.dates`, for a consumer writing their own operation
// against this transport.
export type { DateMask } from "./core/rules/dates.js";
// The decoding a response gets, for JSON a caller already holds: a fixture baked into a bundle,
// a record read back from a cache.
export { decodeRestSchema } from "./core/rules/decode.js";
export {
  type ApiErrorDetails,
  type ErrorCode,
  type ErrorEntry,
  type ErrorMetadata,
  // Published for a consumer writing their own transport-shaped layer: a streaming reader, a
  // proxy, a test double. They hold a status and no response body, which is the one case the
  // error constructors cannot serve on their own.
  errorForStatus,
  type IdempotentAnswer,
  // The structural predicates, for a caller whose value may come from a second copy of this
  // package, where `instanceof` answers false.
  isMountayaApiError,
  isMountayaError,
  MountayaApiError,
  MountayaAuthenticationError,
  MountayaBadRequestError,
  MountayaBillingError,
  MountayaConfigurationError,
  MountayaConflictError,
  MountayaConnectionError,
  MountayaDataError,
  MountayaError,
  MountayaForbiddenError,
  MountayaIdempotencyKeyInUseError,
  MountayaIdempotentAnswerUnavailableError,
  MountayaMethodNotAllowedError,
  MountayaNotFoundError,
  MountayaPayloadTooLargeError,
  MountayaPreconditionFailedError,
  MountayaRateLimitError,
  MountayaServerError,
  PAYMENT_REQUIRED_CODE,
} from "./core/rules/errors.js";
// A paged listing read whole: the walk over its cursors, written once.
export { type PagedResponse, type PaginateOptions, paginate } from "./core/rules/paginate.js";
// The rate-limit state an answer describes, for an answer this client did not read itself: a
// tile a map library fetched, say.
export {
  parseRateLimit,
  type RateLimitInfo,
  type RateLimitPolicy,
} from "./core/rules/rateLimit.js";
// The resources a cache entry is keyed by, for a binding that keeps one per resource.
export type { ResourceKind, ResourceRef } from "./core/rules/resourceRef.js";
export type { RetryOptions, Surface } from "./core/rules/retry.js";
// Data API field selection. `Selection` and `Selected` are the two halves a caller names
// when it holds a selection in a variable rather than passing it inline.
export {
  type DataOptions,
  type DataResult,
  type FieldTree,
  MountayaSelectionError,
  type Selected,
  type Selection,
  type SelectionValue,
} from "./core/rules/selection.js";
export {
  type BaseUrlOverrides,
  type BinaryBody,
  boundFetch,
  type ClientOptions,
  defaultBaseUrls,
  MountayaTransport,
  // The two halves of one call: `RequestArgs` is what an OPERATION builds and `RequestOptions`
  // is what a CALLER passes. The first is published because a consumer writing their own
  // operation against this transport needs it; the second because every generated signature
  // takes one.
  type RequestArgs,
  type RequestOptions,
  type ResponseReport,
  type StreamSeam,
} from "./core/rules/transport.js";
export { SCHEMA_VERSION } from "./core/rules/version.gen.js";
// The free operation functions, for callers who want the smallest possible bundle.
// Importing one of these pulls in the transport and nothing else.
export * as data from "./data/operations.gen.js";
// The generated wire types, so a caller can name what it receives.
export type * as DataSchema from "./data/schema.gen.js";
// The answer of each Data API operation, under a name of its own beside the namespace. A
// module that emits declarations names an inferred answer through a name an entry point
// publishes directly, and a member of `DataSchema` is no such name, so without these an
// exported function that returns `mountaya.data.geocode(...)` unannotated does not compile
// (TS2883). The names are the contract's, and they are the answers alone: the rest of the
// Data API's types share names with the Map Studio's, which hold the root.
// `namespaces.test.ts` derives the list from the operations and holds both entries to it.
export type {
  AnalyzeGeometryResult,
  DirectionsResult,
  ExportItineraryResult,
  GeocodeResult,
  IsochronesResult,
  ItineraryResult,
  MatrixResult,
  PoisResult,
  ReverseGeocodeResult,
  SearchResult,
  SuggestionsResult,
  TerrainGridResult,
  TerrainProfileResult,
  TerrainSamplesResult,
} from "./data/schema.gen.js";
// The selectable field tree of each Data API operation, plus a named `select` type per
// operation (`DirectionsSelection` and its siblings).
export * as DataSelection from "./data/selection.gen.js";
export * as rest from "./rest/operations.gen.js";
// Every resource, params and enum type on the Map Studio surface, at the package root, so
// a caller writes `ItineraryWithDetails` rather than reaching through a namespace for the
// type an operation just handed them. Generated from the contract, so the list follows it.
export type * from "./rest/resources.gen.js";
export type * as RestSchema from "./rest/schema.gen.js";
export * as tiles from "./tiles/operations.gen.js";
export type * as TileSchema from "./tiles/schema.gen.js";
// The geometry a Map Studio answer holds. It is declared by `geojson`, a dependency of this
// package, and a declaration file names an inferred answer through a name an entry point
// publishes: without this one, an exported function that returns `getItinerary(...)`
// unannotated does not compile (TS2883) where the installer keeps `@types/geojson` out of the
// consumer's own directory.
export type { Geometry };

/**
 * Mountaya is the client. One instance holds one credential and one set of policies.
 */
export class Mountaya {
  /**
   * The Map Studio API's organization half: keys, settings, members and usage. Each method
   * resolves to the response envelope, with the payload at `data`.
   */
  readonly admin: ReturnType<typeof bindRest>["admin"];
  /**
   * The AI agent: starting a run, answering it, rating it. Each method resolves to the response
   * envelope, with the payload at `data`; a run's events are read through
   * `createAgentTransport` of `@mountaya/client/events`.
   */
  readonly agent: ReturnType<typeof bindRest>["agent"];
  /**
   * The GraphQL Data API: directions, search, terrain, isochrones, matrices. Each method resolves
   * to the operation's own field, with no envelope.
   */
  readonly data: ReturnType<typeof bindData>;
  /**
   * The Map Studio API's map half: itineraries, zones, collections, and their catalogs. Each
   * method resolves to the response envelope, with the payload at `data`.
   */
  readonly studio: ReturnType<typeof bindRest>["studio"];
  /** The Tile API's style specifications. Each method resolves to the response envelope. */
  readonly tiles: ReturnType<typeof bindTiles>;
  /**
   * The transport every method calls through: for a call this client binds no method for, and
   * for the openers of `@mountaya/client/events`.
   */
  readonly transport: MountayaTransport;

  /**
   * Builds a client over one credential and its options, checking both before anything is sent.
   *
   * @throws `MountayaConfigurationError` for options that are not an object, a credential that
   *   cannot work, a secret key in a page or a React Native application, or an option outside
   *   its range: see {@link ClientOptions}.
   */
  constructor(options: ClientOptions) {
    this.transport = new MountayaTransport(options);

    // One bind call, destructured: studio, admin and agent are three views of a single
    // surface (same base URL, same quota domain), so binding once per view would build the
    // whole table three times to hand back thirds of it.
    const api = bindRest(this.transport);

    this.admin = api.admin;
    this.agent = api.agent;
    this.data = bindData(this.transport);
    this.studio = api.studio;
    this.tiles = bindTiles(this.transport);
  }
}
